import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supportApi } from '../lib/api';
import type {
  CreateTicketPayload,
  CreateMessagePayload,
  AIChatMessage,
} from '../types/support';

export const supportKeys = {
  all: ['support'] as const,
  tickets: () => [...supportKeys.all, 'tickets'] as const,
  ticketList: (filters?: Record<string, string>) => [...supportKeys.tickets(), filters] as const,
  ticket: (id: string) => [...supportKeys.tickets(), id] as const,
  messages: (ticketId: string) => [...supportKeys.ticket(ticketId), 'messages'] as const,
};

export function useTickets(filters?: Record<string, string>) {
  return useQuery({
    queryKey: supportKeys.ticketList(filters),
    queryFn: () => supportApi.getSupportTickets(filters),
    refetchInterval: 30_000,
  });
}

export function useTicket(id: string | null) {
  return useQuery({
    queryKey: supportKeys.ticket(id!),
    queryFn: () => supportApi.getSupportTicket(id!),
    enabled: !!id,
    refetchInterval: 15_000,
  });
}

export function useTicketMessages(ticketId: string | null) {
  return useQuery({
    queryKey: supportKeys.messages(ticketId!),
    queryFn: () => supportApi.getTicketMessages(ticketId!),
    enabled: !!ticketId,
    refetchInterval: 10_000,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTicketPayload) => supportApi.createSupportTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
    },
  });
}

export function useCreateMessage(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateMessagePayload) => supportApi.createTicketMessage(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
    },
  });
}

export function useCloseTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => supportApi.closeTicket(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
    },
  });
}

export function useReopenTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => supportApi.reopenTicket(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
    },
  });
}

export function useResolveTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => supportApi.resolveTicket(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
    },
  });
}

export function useAssignTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: number | null }) =>
      supportApi.assignTicket(id, userId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: supportKeys.ticket(id) });
      queryClient.invalidateQueries({ queryKey: supportKeys.tickets() });
    },
  });
}

export function useAIChat() {
  return useMutation({
    mutationFn: ({
      message,
      history,
      conversationId,
    }: {
      message: string;
      history: AIChatMessage[];
      conversationId?: string | null;
    }) => supportApi.sendAIChatMessage(message, history, conversationId),
  });
}

// ─── AI Conversation Hooks ──────────────────────────────────────────────────

export const aiConversationKeys = {
  all: ['ai-conversations'] as const,
  list: () => [...aiConversationKeys.all, 'list'] as const,
  detail: (id: string) => [...aiConversationKeys.all, id] as const,
  active: () => [...aiConversationKeys.all, 'active'] as const,
};

export function useAIConversations() {
  return useQuery({
    queryKey: aiConversationKeys.list(),
    queryFn: () => supportApi.getAIConversations(),
  });
}

export function useAIConversation(id: string | null) {
  return useQuery({
    queryKey: aiConversationKeys.detail(id!),
    queryFn: () => supportApi.getAIConversation(id!),
    enabled: !!id,
  });
}

export function useActiveAIConversation() {
  return useQuery({
    queryKey: aiConversationKeys.active(),
    queryFn: () => supportApi.getActiveAIConversation(),
  });
}

export function useDeleteAIConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => supportApi.deleteAIConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiConversationKeys.all });
    },
  });
}
